<template>
  <div class="base-input-number-decimal">
    <div class="flex">
      <button class="button-qty p0" @click="minusQtyProduct" >
        <span class="material-icons qty-icon">remove</span>
      </button>
      <input
        v-model="customValue"
        :ref="'input'+sku"
        type="number"
        :min="min"
        :disabled="onDisable"
        :focus="autofocus"
        :step="0.1"
        class="m0 no-outline base-input-number-decimal__input brdr-cl-primary bg-cl-transparent h4"
        @input="onInput"
        @blur="$emit('blur', $event.target.value)"
        @change="fixQty"
        data-hj-whitelist
      >
      <button  class="button-qty p0" @click="plusQtyProduct" >
        <span class="material-icons qty-icon">add</span>
      </button>
    </div>
    <ValidationMessages v-if="validations" :validations="validations" />
  </div>
</template>

<script>
import { debounce } from 'lodash';

const ValidationMessages = () => import(/* webpackChunkName: "vsf-validation-messages" */ 'theme/components/core/blocks/Form/ValidationMessages.vue');

export default {
  name: 'BaseInputDecimalNumber',
  components: {
    ValidationMessages
  },
  data () {
    return {
      customValue: '',
      inputStatus: false,
      weightDropdown: [],
      debounceInputPrice: () => {}
    }
  },
  mounted () {
    for (let i = 0.1; i <= 1; i += 0.1) {
      this.weightDropdown.push(i.toFixed(2))
    }
    this.customValue = this.isInt(this.value) ? this.value : parseFloat(this.value.toFixed(2))
    this.inputStatus = false
    this.debounceInputPrice = debounce(this.inputPrice, 800)
  },
  computed: {
    refName () {
      return 'input' + this.sku;
    },
    currentQty () {
      if (this.useQtyInCart) {
        if (isNaN(this.qtyInCart)) {
          return this.qty;
        } else {
          let correctQtyInCart = parseFloat(this.qtyInCart);
          let currentQty = this.qty - correctQtyInCart;
          if (currentQty > this.min) {
            return currentQty;
          } else {
            return this.min;
          }
        }
      } else {
        return this.qty
      }
    },
    qtyIncrement () {
      return this.qtyIncrements ? this.qtyIncrements : 0.1
    }
  },
  watch: {
    sku () {
      this.customValue = 0.1;
      this.inputStatus = false
    },
    currentQty () {
      let floatCustomValue = parseFloat(this.customValue);
      if (floatCustomValue > this.currentQty) {
        let correctCurrentQty = Math.floor(this.currentQty * 100) / 100;
        this.customValue = correctCurrentQty;
      }
      this.$emit('refresh', { newValue: this.customValue });
    }
  },
  methods: {
    onBlur (event) {
      let value = event.target.value
      if (value === 'custom') {
        this.inputStatus = true;
        this.customValue = this.value
        this.$emit('blur', this.value)
        this.$refs[this.refName].focus();
      } else {
        this.customValue = value
        this.$emit('blur', value)
      }
    },
    onInput (event) {
      let value = event.target.value
      if (value === 'custom') {
        this.inputStatus = true;
        this.customValue = this.value
        this.$emit('input', this.customValue)
      } else {
        value = value >= this.currentQty ? this.currentQty : value
        this.customValue = value
        this.debounceInputPrice()
      }
    },
    minusQtyProduct () {
      if (this.customValue <= this.min) {
        return true
      } else {
        this.customValue = this.customValue - this.qtyIncrement < this.min ? this.min : this.customValue - this.qtyIncrement;
        this.customValue = Math.round(this.customValue * 100) / 100;
        this.$emit('input', this.customValue);
      }
    },
    plusQtyProduct () {
      if (this.customValue >= this.currentQty) {
        return true
      } else {
        this.customValue += this.qtyIncrement;
        this.customValue = Math.round(this.customValue * 100) / 100;
        this.$emit('input', this.customValue);
      }
    },
    fixQty () {
      if (isNaN(this.value) || this.value < this.min) {
        this.customValue = this.min;
      } else {
        this.customValue = Math.round(this.customValue * 100) / 100;
      }
      this.$emit('refresh', { newValue: this.customValue });
    },
    isInt (n) {
      return n % 1 === 0;
    },
    inputPrice () {
      if (this.customValue < this.min) {
        this.customValue = this.min
      }
      if (this.customValue / this.min % 2 !== 0) {
        this.customValue = Math.trunc(this.customValue / this.min) * this.min
      }
      this.customValue = Math.round(this.customValue * 100) / 100;
      this.$emit('input', this.customValue)
    }
  },
  props: {
    value: {
      type: [String, Number],
      default: 0
    },
    qty: {
      type: [Number],
      default: 1
    },
    sku: {
      type: [String],
      required: true
    },
    qtyInCart: {
      required: true
    },
    qtyIncrements: {
      required: true,
      default: 0.1
    },
    onDisable: {
      type: Boolean,
      default: false
    },
    useQtyInCart: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    min: {
      type: Number,
      default: 0
    },
    sum: {
      type: Number,
      default: 0
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    validations: {
      type: Array,
      default: () => []
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';


  .button-qty {
    border: none;
    background: none;
    color: #000;
  }
  .base-input-number-decimal {
    &__input {
      font-family: 'TTNorms Regular', sans-serif;
      text-align: center;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: 0.09px;
      color: #111111;
      border-style: solid;
      border-width: 0 0 0 0;
      width: 40px;
      height: 1.4rem;
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    &__label {
      font-size: 0.8rem;
      line-height: 1.2rem;
      max-width: 100px;
      @media (min-width: 769px) {
        font-size: 1rem;
        line-height: 1.4rem;
      }
    }
  }

  .select {
    width: 100%;
  }

  .backButton {
    font-size: 16px;
    color: white;
    border: 0px;
    border-radius: 0px;
    background-color: #4F4F4F;
  }
  .qty-icon {
    display: inline-block;
    width: 22px;
    height: 22px;
    font-size: 20px;
    color: #11bb2e;
    border: 1px solid #11bb2e;
    border-radius: 50%;
    overflow: hidden;
  }
</style>
